/** Разметка текста: одна и та же в чате и в вебе.
 *
 * Тексты, которые пишет админ — приветствие меню, сообщение кнопки, подпись под
 * разделом, рассылка, — уходят и в чат, и в витрину. В чате их размечает сам
 * Telegram, поэтому набор тегов берём его: что он понимает, то и умеем. Так
 * админ пишет один раз, а выглядит одинаково в обоих местах.
 *
 * Рисуем разбором в узлы React, а не подстановкой в `innerHTML`. Текст пришёл
 * от человека и хранится в базе; единственный способ не думать каждый раз, не
 * пролезет ли туда чужой скрипт, — вообще не давать браузеру разбирать чужую
 * строку как разметку.
 */
import type { ReactNode } from 'react';

/** Теги, которые точно переживут отправку в Telegram. */
export const RICH_TAGS = [
  { tag: 'b', label: 'Ж', title: 'Жирный', className: 'font-extrabold' },
  { tag: 'i', label: 'К', title: 'Курсив', className: 'italic' },
  { tag: 'u', label: 'Ч', title: 'Подчёркнутый', className: 'underline' },
  { tag: 's', label: 'З', title: 'Зачёркнутый', className: 'line-through' },
  { tag: 'code', label: '‹›', title: 'Моноширинный', className: 'font-mono' },
  { tag: 'tg-spoiler', label: '👁', title: 'Скрытый текст', className: '' },
] as const;

const KNOWN = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del',
                       'code', 'pre', 'tg-spoiler', 'a']);

/** Ссылка ведёт в сеть или в Telegram — и никуда больше. */
function safeHref(raw: string): string | null {
  const url = raw.trim();
  return /^(https?:\/\/|tg:\/\/|mailto:)/i.test(url) ? url : null;
}

interface Token {
  tag: string;
  href?: string;
  children: ReactNode[];
}

/** Текст с переводами строк — в узлы: `\n` внутри JSX сам по себе не перенос. */
function lines(text: string, key: string): ReactNode[] {
  const parts = text.split('\n');
  const out: ReactNode[] = [];
  parts.forEach((part, index) => {
    if (index) out.push(<br key={`${key}-br-${index}`} />);
    if (part) out.push(part);
  });
  return out;
}

/** Обернуть накопленное в нужный тег. Незнакомое остаётся текстом. */
function wrap(token: Token, key: string): ReactNode {
  const kids = token.children;
  switch (token.tag) {
    case 'b':
    case 'strong':
      return <b key={key}>{kids}</b>;
    case 'i':
    case 'em':
      return <i key={key}>{kids}</i>;
    case 'u':
      return <u key={key}>{kids}</u>;
    case 's':
    case 'strike':
    case 'del':
      return <s key={key}>{kids}</s>;
    case 'code':
      return (
        <code key={key} className="rounded bg-glass px-1 py-0.5 font-mono text-[.92em]">
          {kids}
        </code>
      );
    case 'pre':
      return (
        <pre key={key} className="my-1 overflow-x-auto rounded-lg bg-glass p-2.5 font-mono text-[.9em]">
          {kids}
        </pre>
      );
    case 'tg-spoiler':
      // В чате спойлер открывается тапом; здесь — тем же движением
      return (
        <span
          key={key}
          className="cursor-pointer rounded bg-glass-2 px-0.5 text-transparent transition-colors duration-200 select-none hover:text-frost focus:text-frost"
          tabIndex={0}
          onClick={(event) => {
            event.currentTarget.classList.remove('text-transparent', 'bg-glass-2', 'select-none');
          }}
        >
          {kids}
        </span>
      );
    case 'a': {
      const href = token.href ? safeHref(token.href) : null;
      if (!href) return <span key={key}>{kids}</span>;
      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-link underline decoration-link/40 underline-offset-2"
        >
          {kids}
        </a>
      );
    }
    default:
      return <span key={key}>{kids}</span>;
  }
}

/**
 * Разобрать текст с разметкой Telegram в узлы React.
 *
 * Всё, что не входит в известный набор, остаётся обычным текстом — в описании
 * товара вполне может встретиться «<» как знак сравнения, и превращать его в
 * тег было бы неверно.
 */
export function renderText(source: string): ReactNode[] {
  const text = source ?? '';
  const root: Token = { tag: 'root', children: [] };
  const stack: Token[] = [root];
  const top = () => stack[stack.length - 1] as Token;

  let cursor = 0;
  let key = 0;
  const pattern = /<(\/?)([a-z-]+)((?:\s+href\s*=\s*"[^"]*")?)\s*>/gi;

  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    const [whole, closing, name, attrs] = match;
    const tag = (name ?? '').toLowerCase();
    if (!KNOWN.has(tag)) continue; // не наш тег — пусть остаётся текстом

    if (match.index > cursor) {
      top().children.push(...lines(text.slice(cursor, match.index), `t${key++}`));
    }
    cursor = match.index + whole.length;

    if (closing) {
      // Закрыли не то, что открывали — ищем ближайший подходящий уровень
      const at = [...stack].reverse().findIndex((item) => item.tag === tag);
      if (at === -1) continue;
      for (let depth = 0; depth <= at; depth++) {
        const done = stack.pop() as Token;
        top().children.push(wrap(done, `k${key++}`));
      }
    } else {
      const href = /href\s*=\s*"([^"]*)"/i.exec(attrs ?? '')?.[1];
      stack.push({ tag, href, children: [] });
    }
  }

  if (cursor < text.length) {
    top().children.push(...lines(text.slice(cursor), `t${key++}`));
  }
  // Незакрытые теги закрываем сами: текст важнее строгости разметки
  while (stack.length > 1) {
    const done = stack.pop() as Token;
    top().children.push(wrap(done, `k${key++}`));
  }
  return root.children;
}

/** Готовый блок текста с разметкой — то, что видит клиент. */
export function RichBody({ text, className = '' }: { text: string; className?: string }) {
  return <div className={`leading-relaxed break-words ${className}`}>{renderText(text)}</div>;
}
